// @/pages/landing/components/UploadTypeDialog.tsx

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import Button from "@/components/ui/Button";
import { ButtonStyleType } from "@/types/shared/uiComponents";
import { useAppTheme } from "@/hooks/useAppTheme";
import CloseIcon from "@mui/icons-material/Close";
import { useGetList, useGetOne } from "react-admin";
import { BORDER_RADIUS } from "@/constants/layout";
import {
  DropzoneDocumentType,
  PolicyStatus,
  ResourceName,
  TargetSchema,
  type Client,
  type DocumentRead,
  type Policy,
  type PolicyVendorContext,
  type Vendor,
} from "@/types";
import { getResourceIcon } from "@/utils/icons";
import { ClientSearchField, DocumentDropzone, type FileClientChoice } from "@/components/common/DocumentDropzone";
import { buildPolicyVendorContext } from "@/components/common/DocumentDropzone/policyVendorSelector";
import { formatYearRange } from "@/utils/formatters/dateFormatter";
import { useNavigate } from "react-router";
import { featureFlags } from "@/utils/featureFlags";
import { waitForDocumentReady } from "@/resources/documents/hooks/useDocumentReady";

const ALL_UPLOAD_OPTIONS = [
  {
    type: DropzoneDocumentType.CLAIMS,
    label: "Claims file",
    description: "Paid claim documentation (TPA/PBM)",
    icon: getResourceIcon(ResourceName.STOPLOSS_CLAIMS),
    targetSchema: TargetSchema.STOPLOSS_CLAIM,
  },
  {
    type: DropzoneDocumentType.REIMBURSEMENTS,
    label: "Reimbursement",
    description: "Carrier reimbursement files",
    icon: getResourceIcon(ResourceName.REIMBURSEMENTS),
    targetSchema: TargetSchema.REIMBURSEMENT,
  },
  {
    type: DropzoneDocumentType.POLICIES,
    label: "Policy",
    description: "Policies and renewal documents",
    icon: getResourceIcon(ResourceName.POLICIES),
    targetSchema: TargetSchema.POLICY,
  },
];

type UploadOption = (typeof ALL_UPLOAD_OPTIONS)[number];

// Policy ingestion (extraction → loader → benefits/endorsements) is gated
// behind featureFlags.policyIngestionEnabled until the full v1 set
// (AB#4619 / AB#4621 / AB#4625 / AB#4630 / AB#4631) is in production.
// Hiding the option prevents users from invoking a half-built pipeline.
// PolicyPdfUpload on the policy show page is unaffected — that uses
// storage_only and is for attaching reference docs to existing policies.
//
// Read the flag at render time so test code can mock the featureFlags
// module and observe both states without fighting Vite/Vitest module
// caching. Same runtime behavior: env-driven boolean is stable for the
// lifetime of the SPA.
const visibleUploadOptions = (): UploadOption[] =>
  featureFlags.policyIngestionEnabled
    ? ALL_UPLOAD_OPTIONS
    : ALL_UPLOAD_OPTIONS.filter((o) => o.type !== DropzoneDocumentType.POLICIES);

/** Where the app goes once an upload batch settles. */
export type PostUploadDestination = { kind: "show"; documentId: string } | { kind: "list" } | { kind: "stay" };

/**
 * One document created → open it; the uploader's next job is on that page, not
 * in the list. Anything else (several files, a workbook fanned into sheets,
 * nothing created) → the list when this dialog owns navigation, or stay put
 * when the caller gave an `onUploadComplete` (it is already on the page it wants,
 * e.g. the Documents list refetching). Pure so the rule is unit-testable.
 */
export const resolvePostUploadDestination = (
  uploaded: DocumentRead[],
  hasCompletionCallback: boolean
): PostUploadDestination => {
  if (uploaded.length === 1 && uploaded[0].id) return { kind: "show", documentId: String(uploaded[0].id) };
  return hasCompletionCallback ? { kind: "stay" } : { kind: "list" };
};

/** Answer to "Who are these files for?" (#7551 AC3; BOB per ADR-057). */
export type ClientMode = "client" | "bob" | "later";

/** The dialog's screens, in order. From a Client page "client" is pre-answered and skipped. */
export type UploadStep = "type" | "client" | "files";

export const resolveUploadSteps = (clientLocked: boolean): UploadStep[] =>
  clientLocked ? ["type", "files"] : ["type", "client", "files"];

/** Label for a step once it is done, so the step row reads like a receipt of the answers. */
export const doneStepLabel = (
  step: UploadStep,
  answers: { typeLabel: string; clientName: string | null; clientMode: ClientMode }
): string => {
  if (step === "type") return answers.typeLabel;
  if (step === "client") {
    if (answers.clientMode === "bob") return "Multiple clients";
    if (answers.clientMode === "later") return "Decide at review";
    return answers.clientName ?? "Client";
  }
  return "Files";
};

/** Per-document-type default vendor mapping (e.g. from client's active policy) */
export interface DefaultVendorMap {
  /** Default carrier vendor for reimbursement and policy uploads */
  carrier?: string;
}

/** Vendor context derived from a client's policies — what ClientShow computes for its page. */
interface ClientUploadContext {
  defaultVendors?: DefaultVendorMap;
  policyVendors?: PolicyVendorContext;
  sourceLabel: string;
  /** "Anthem BCBS 2026" when the client has one active policy; null otherwise. */
  policyLabel: string | null;
}

/**
 * The vendor context for a client picked inside the dialog (#7551 AC4): the
 * client's ACTIVE policies, unioned, the same way ClientShow scopes "All
 * Policies" — so the single-primary rule evaluates at the client grain and a
 * multi-administrator client never gets a confidently wrong pre-fill (#5591).
 * Disabled (no fetch) until a client is picked.
 */
const useClientUploadContext = (clientId: string | undefined): ClientUploadContext => {
  const { data: policies = [] } = useGetList<Policy>(
    ResourceName.POLICIES,
    {
      filter: { client_id_ref: clientId },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "effective_start_date", order: "DESC" },
    },
    { enabled: !!clientId }
  );
  const active = useMemo(() => policies.filter((p) => p.status === PolicyStatus.ACTIVE), [policies]);
  const singlePolicy = active.length === 1 ? active[0] : undefined;
  const { data: carrier } = useGetOne<Vendor>(
    ResourceName.VENDORS,
    { id: singlePolicy?.vendor_id_ref ?? "" },
    { enabled: !!singlePolicy?.vendor_id_ref }
  );

  return useMemo(() => {
    const carrierName = carrier?.company_name || singlePolicy?.source_data?.policy_carrier;
    const policyLabel = singlePolicy
      ? [carrierName, formatYearRange(singlePolicy.effective_start_date, singlePolicy.effective_end_date)]
          .filter(Boolean)
          .join(" ") || null
      : null;
    return {
      defaultVendors: singlePolicy ? { carrier: singlePolicy.vendor_id_ref } : undefined,
      policyVendors: active.length > 0 ? buildPolicyVendorContext(active) : undefined,
      sourceLabel: policyLabel ?? "the client's active policies",
      policyLabel,
    };
  }, [active, singlePolicy, carrier]);
};

interface UploadTypeDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Fired after the batch settles with the documents it created. A single
   * created document always opens its show page afterwards; otherwise the
   * dialog navigates to /documents only when this callback is absent.
   */
  onUploadComplete?: (uploaded: DocumentRead[]) => void;
  /** Pre-select vendors per document type in the assignment dialog */
  defaultVendors?: DefaultVendorMap;
  /**
   * Policy vendor context for the claims selector (#5591): pins the policy's TPA/Rx
   * vendors, tags run-out, and pre-fills the primary TPA. Applied only to the claims
   * upload path. When absent and a client is picked in the dialog, derived from that
   * client's active policies (#7551 AC4).
   */
  policyVendors?: PolicyVendorContext;
  /** Names the source of the claims prefill in the dialog (e.g. a policy id, or the client's administrators). */
  defaultSourceLabel?: string;
  /**
   * Bind every uploaded document to this client (e.g. uploading from the client
   * screen). The Client field then shows the client as text and cannot be changed
   * (#7551 AC3); without it the field is a picker.
   */
  clientIdRef?: string;
  /** Display name for `clientIdRef`. Fetched when omitted. */
  clientLabel?: string;
}

export const UploadTypeDialog = ({
  open,
  onClose,
  onUploadComplete,
  defaultVendors,
  policyVendors,
  defaultSourceLabel,
  clientIdRef,
  clientLabel,
}: UploadTypeDialogProps) => {
  const navigate = useNavigate();
  const { isDarkMode } = useAppTheme();
  const uploadOptions = visibleUploadOptions();
  const lockedClient = !!clientIdRef;
  const steps = resolveUploadSteps(lockedClient);

  const [stepIndex, setStepIndex] = useState(0);
  const [selectedType, setSelectedType] = useState<UploadOption>(uploadOptions[0]);
  // True while the prepare rows show on the files step — the panel owns the footer then.
  const [preparing, setPreparing] = useState(false);
  // True while files are uploading: Escape, backdrop and X do nothing then (#7551
  // AC11). Closing mid-flight unmounted the dropzone while the promises kept
  // running, and the completion handler then navigated from wherever the user
  // had gone (!4974 thread 31127).
  const [uploading, setUploading] = useState(false);
  // Client picked inside the dialog (picker mode only).
  const [pickedClient, setPickedClient] = useState<FileClientChoice | null>(null);
  // "A client" (default — steer towards picking one), "Multiple clients in one file", or "Decide at review".
  const [clientMode, setClientMode] = useState<ClientMode>("client");
  // Cancels an in-flight "wait for the new document, then open it" when this
  // dialog unmounts, so a navigation elsewhere mid-wait can't be yanked away.
  const openWhenReadyAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => openWhenReadyAbortRef.current?.abort(), []);

  const step: UploadStep = steps[Math.min(stepIndex, steps.length - 1)];

  const { data: lockedClientRecord } = useGetOne<Client>(
    ResourceName.CLIENTS,
    { id: clientIdRef ?? "" },
    { enabled: open && lockedClient && !clientLabel }
  );
  const lockedClientName = clientLabel ?? lockedClientRecord?.client_legal_name;

  // Picker mode derives the vendor context from the picked client's policies;
  // locked mode uses what the caller computed (policy-selector aware on ClientShow).
  const derived = useClientUploadContext(lockedClient ? undefined : pickedClient?.id);
  const effectivePolicyVendors = lockedClient ? policyVendors : derived.policyVendors;
  const effectiveDefaultVendors = lockedClient ? defaultVendors : derived.defaultVendors;
  const effectiveSourceLabel = lockedClient ? defaultSourceLabel : derived.sourceLabel;

  // The batch's client as the dropzone sees it: locked → the caller's; picker →
  // the pick, or null for "decide at review" / book of business.
  const batchClient: FileClientChoice | null = lockedClient
    ? { id: clientIdRef!, name: lockedClientName ?? "" }
    : clientMode === "client"
      ? pickedClient
      : null;

  // The policy label shown beside the client: the caller's source label when it
  // names a policy (ClientShow passes the carrier + year), else the derived one.
  const policyLabel = lockedClient
    ? defaultSourceLabel && !defaultSourceLabel.startsWith("the client's")
      ? defaultSourceLabel
      : null
    : derived.policyLabel;

  const isClaims = selectedType.type === DropzoneDocumentType.CLAIMS;
  // Book-of-business files are a reimbursement concept (#5309 / ADR-057).
  const multiClientEligible = selectedType.targetSchema === TargetSchema.REIMBURSEMENT;

  const resetFlow = () => {
    setStepIndex(0);
    setPreparing(false);
    setPickedClient(null);
    setClientMode("client");
  };

  const handleClose = () => {
    if (uploading) return;
    resetFlow();
    onClose();
  };

  const chooseType = (option: UploadOption) => {
    setSelectedType(option);
    if (clientMode === "bob" && option.targetSchema !== TargetSchema.REIMBURSEMENT) setClientMode("client");
    setStepIndex(1);
  };

  const goBack = () => setStepIndex((i) => Math.max(0, i - 1));
  const goNext = () => setStepIndex((i) => Math.min(steps.length - 1, i + 1));
  /** "Decide at review" or "Multiple clients in one file": clears any pick and advances. */
  const answerClient = (mode: ClientMode) => {
    setClientMode(mode);
    setPickedClient(null);
    goNext();
  };

  /**
   * A freshly accepted document 404s on GET for a moment while the partner
   * association finalizes (#5347), and react-admin's show controller answers a
   * 404 by bouncing to the list with "item doesn't exist". So wait for the
   * record to be fetchable before opening it; fall back to the list if the wait
   * fails or times out and nobody else owns navigation.
   */
  const openWhenReady = async (documentId: string) => {
    openWhenReadyAbortRef.current?.abort();
    const controller = new AbortController();
    openWhenReadyAbortRef.current = controller;
    // The show page only needs the record to exist; a file already extracting
    // (PROCESSING) is as good as UPLOADED here.
    const outcome = await waitForDocumentReady(documentId, { signal: controller.signal, readyWhen: "fetchable" });
    if (outcome === "aborted") return;
    if (outcome === "ready") {
      void navigate(`/documents/${documentId}/show`);
    } else if (!onUploadComplete) {
      void navigate("/documents");
    }
  };

  const handleUploadComplete = (uploaded: DocumentRead[] = []) => {
    setUploading(false);
    resetFlow();
    onClose();
    onUploadComplete?.(uploaded);

    const destination = resolvePostUploadDestination(uploaded, !!onUploadComplete);
    if (destination.kind === "show") {
      void openWhenReady(destination.documentId);
    } else if (destination.kind === "list") {
      void navigate("/documents");
    }
  };

  const destinationLine =
    step !== "files"
      ? null
      : batchClient?.name
        ? [batchClient.name, policyLabel].filter(Boolean).join(" · ")
        : clientMode === "bob"
          ? "Multiple clients in one file"
          : "Client decided at review";

  const stepAnswers = { typeLabel: selectedType.label, clientName: batchClient?.name ?? null, clientMode };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: BORDER_RADIUS.LARGE, maxWidth: 760 },
      }}
    >
      <DialogTitle component="div" sx={{ pb: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h3">
              {step === "type" ? "Upload documents" : `Upload ${selectedType.label.toLowerCase()}`}
            </Typography>
            {destinationLine && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {destinationLine}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ mt: -0.5 }} aria-label="Close" disabled={uploading}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <UploadStepper
          steps={steps}
          activeIndex={stepIndex}
          doneLabel={(s) => doneStepLabel(s, stepAnswers)}
          lockedClientName={lockedClient ? (lockedClientName ?? null) : null}
        />

        {step === "type" && (
          <Box sx={{ mt: 2.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              What are you uploading?
            </Typography>
            <Stack spacing={1.5}>
              {uploadOptions.map((option) => {
                const selected = option.type === selectedType.type;
                return (
                  <Box
                    key={option.type}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selected}
                    onClick={() => chooseType(option)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        chooseType(option);
                      }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 2.5,
                      borderRadius: BORDER_RADIUS.MEDIUM,
                      border: "1px solid",
                      borderColor: "divider",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      "&:hover, &:focus-visible": {
                        bgcolor: "action.hover",
                        borderColor: "primary.main",
                        outline: "none",
                        "& .upload-icon": { bgcolor: "primary.main", color: "primary.contrastText" },
                      },
                    }}
                  >
                    <Box
                      className="upload-icon"
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: BORDER_RADIUS.MEDIUM,
                        bgcolor: isDarkMode ? "grey.800" : "grey.100",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.15s ease",
                        flexShrink: 0,
                      }}
                    >
                      <option.icon sx={{ fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="body1" fontWeight={600}>
                        {option.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        {step === "client" && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Who are these files for?
            </Typography>
            {/* Picking a client advances, like the cards; "Decide at review" is the
                real alternative beside the field (#7551 AC3). */}
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <ClientSearchField
                  value={pickedClient}
                  onChange={(c) => {
                    setPickedClient(c);
                    if (c) {
                      setClientMode("client");
                      goNext();
                    }
                  }}
                  placeholder="Search clients…"
                  size="medium"
                  autoFocus
                />
              </Box>
              <Button variant={ButtonStyleType.Outlined} onClick={() => answerClient("later")}>
                Decide at review
              </Button>
            </Stack>
            {multiClientEligible && (
              <Button
                variant={ButtonStyleType.Text}
                onClick={() => answerClient("bob")}
                sx={{ mt: 1.5, px: 0.5, textAlign: "left" }}
              >
                Multiple clients in one file (book of business) →
              </Button>
            )}
          </Box>
        )}

        {step === "files" && (
          <Box sx={{ mt: 2.5, mb: 1 }}>
            {/* Keyed on the type so a type change starts a fresh dropzone (accepted
                formats and vendor list differ per type, #7551 AC2). */}
            <DocumentDropzone
              key={selectedType.type}
              documentType={selectedType.type}
              targetSchema={selectedType.targetSchema}
              onUploadComplete={handleUploadComplete}
              defaultVendorId={isClaims ? undefined : effectiveDefaultVendors?.carrier}
              policyVendors={isClaims ? effectivePolicyVendors : undefined}
              defaultSourceLabel={isClaims ? effectiveSourceLabel : undefined}
              onAssignmentDialogChange={setPreparing}
              onUploadingChange={setUploading}
              fileClientEditable={!lockedClient}
              clientIdRef={batchClient?.id}
              defaultClient={batchClient}
              defaultMultiClient={clientMode === "bob"}
              prepareInline
            />
          </Box>
        )}
      </DialogContent>

      {/* The prepare rows carry their own footer (Back · Cancel · Upload). */}
      {!preparing && (
        <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: "space-between" }}>
          <Box>
            {stepIndex > 0 && (
              <Button variant={ButtonStyleType.Text} onClick={goBack} sx={{ color: "text.secondary" }}>
                Back
              </Button>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant={ButtonStyleType.Text} onClick={handleClose}>
              Cancel
            </Button>
          </Stack>
        </DialogActions>
      )}
    </Dialog>
  );
};

/**
 * MUI Stepper over the dialog's screens. Done steps carry the answer given as
 * their optional line ("Client · Acme Manufacturing"), so the stepper is both
 * progress and a recap. A locked client shows as a done step from the start.
 */
const UploadStepper = ({
  steps,
  activeIndex,
  doneLabel,
  lockedClientName,
}: {
  steps: UploadStep[];
  activeIndex: number;
  doneLabel: (step: UploadStep) => string;
  lockedClientName: string | null;
}) => {
  const titles: Record<UploadStep, string> = { type: "Type", client: "Client", files: "Files" };
  const items = steps.map((s, i) => ({
    key: s,
    title: titles[s],
    completed: i < activeIndex,
    active: i === activeIndex,
    answer: i < activeIndex ? doneLabel(s) : null,
  }));
  if (lockedClientName && !steps.includes("client")) {
    items.splice(1, 0, { key: "client", title: "Client", completed: true, active: false, answer: lockedClientName });
  }
  const activeStep = items.findIndex((it) => it.active);
  return (
    <Stepper
      activeStep={activeStep}
      aria-label="Upload steps"
      sx={{
        mt: 1.5,
        mb: 0,
        // Compact: the stepper is a wayfinding line, not a headline.
        "& .MuiStepIcon-root": { width: 20, height: 20 },
        "& .MuiStepIcon-text": { fontSize: "0.7rem" },
        "& .MuiStepLabel-iconContainer": { pr: 0.75 },
        "& .MuiStepLabel-label": { fontSize: 13, lineHeight: 1.3, "&.Mui-active": { fontWeight: 600 } },
        "& .MuiStepLabel-labelContainer": { mt: 0 },
        "& .MuiStepConnector-root": { top: 10 },
      }}
    >
      {items.map((item) => (
        <Step key={item.key} completed={item.completed}>
          <StepLabel
            optional={
              item.answer ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ display: "block", maxWidth: 200, fontSize: 11, lineHeight: 1.2 }}
                >
                  {item.answer}
                </Typography>
              ) : undefined
            }
          >
            {item.title}
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );
};
